/** Auto-play cadence: how long each captured page frame stays on screen. */
export const FRAME_INTERVAL_MS = 1200;

/** Playback state for the gig order page snapshot slideshow. */
export interface SnapshotPlayerState {
  index: number;
  isPlaying: boolean;
  count: number;
}

export type SnapshotPlayerAction =
  | { type: 'reset'; count: number }
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'seek'; index: number }
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'toggle' }
  | { type: 'tick' };

/** Confine a frame index to the valid [0, count-1] range, or 0 when empty. */
export const clampIndex = (index: number, count: number): number => {
  if (count <= 0 || index < 0) {
    return 0;
  }
  return index > count - 1 ? count - 1 : index;
};

/** Human 1-based "position / total" label, e.g. "3 / 12". */
export const frameLabel = (index: number, count: number): string =>
  count <= 0 ? '0 / 0' : `${clampIndex(index, count) + 1} / ${count}`;

/** Format a captured-at ISO timestamp for display, falling back to an em dash. */
export const formatCapturedAt = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const initialSnapshotPlayerState = (count: number): SnapshotPlayerState => ({
  index: 0,
  isPlaying: false,
  count,
});

/** Pure playback transitions; manual navigation pauses, ticks stop at the final frame. */
export const snapshotReducer = (state: SnapshotPlayerState, action: SnapshotPlayerAction): SnapshotPlayerState => {
  switch (action.type) {
    case 'reset':
      return { index: 0, isPlaying: false, count: action.count };
    case 'next':
      return { ...state, index: clampIndex(state.index + 1, state.count), isPlaying: false };
    case 'prev':
      return { ...state, index: clampIndex(state.index - 1, state.count), isPlaying: false };
    case 'seek':
      return { ...state, index: clampIndex(action.index, state.count), isPlaying: false };
    case 'play':
      return { ...state, isPlaying: state.count > 1 };
    case 'pause':
      return { ...state, isPlaying: false };
    case 'toggle':
      return { ...state, isPlaying: state.isPlaying ? false : state.count > 1 };
    case 'tick':
      if (!state.isPlaying) {
        return state;
      }
      if (state.index >= state.count - 1) {
        return { ...state, isPlaying: false };
      }
      return { ...state, index: clampIndex(state.index + 1, state.count) };
    default:
      return state;
  }
};
